import { useApiHealthStore } from '@app/store/apiHealthStore';
import { useParameterStore } from '@app/store/parameterStore';
import { ApiError } from '@app/utils/ApiError';
import { Parameter } from '@app/model/Parameter';
import { useCallback, useEffect } from 'react';

export interface UseParametersResult {
  parameters: Parameter[];
  isLoaded: boolean;
  /** Why the load failed, or null. Every value accessor falls back rather than throwing. */
  error: ApiError | null;
  /** Re-reads the settings, ignoring the cache. Awaitable, so callers can show progress. */
  retry: () => Promise<void>;
}

/**
 * Keeps the runtime settings loaded for as long as something is mounted that reads one.
 *
 * The counterpart of useEntity for a collection that is not an entity. Mount it once high up —
 * AppLayout does — and anything below can then read a value synchronously through the accessors
 * in parameterStore. A component that reads a parameter does not need to call this: it needs
 * somebody to have called it, which is why it lives where the authenticated shell is built.
 *
 * @param forceRefresh for the admin screen, which must show what the service is using right now
 *     rather than what the cache last heard.
 */
export const useParameters = (forceRefresh = false): UseParametersResult => {
  const parameters = useParameterStore(state => state.items);
  const isLoaded = useParameterStore(state => state.isLoaded);
  const error = useParameterStore(state => state.error);
  const load = useParameterStore(state => state.load);

  // Bumped when the backend comes back, or when the user hits retry in the connectivity banner
  const retryToken = useApiHealthStore(state => state.retryToken);

  const retry = useCallback(() => load(true), [load]);

  useEffect(() => {
    // load() never rejects — a failure lands on the store's error, which this hook returns.
    // It also collapses concurrent calls, so several mounted readers make one request.
    void load(forceRefresh);
  }, [load, forceRefresh]);

  // Pick the settings up once the service is reachable again, rather than leaving every reader on
  // its fallback until something else happens to trigger a load.
  useEffect(() => {
    if (retryToken === 0) return;
    void load(true);
  }, [retryToken, load]);

  return { parameters, isLoaded, error, retry };
};
